import {
  createInputSchema,
  type FormConfig,
  getShapeById,
  type ProjectSchemaJSON,
  type PropertySchema,
  type PropertyTransform,
  shapeToPropertySchema,
  transformOneOf
} from '@takeshape/schema';
import { isRecord } from '@takeshape/util';
import type { Content } from 'app/common/utils/object-diff';
import { FORM_NAME } from 'app/content/constants';
import { getForm } from 'app/schema-form/selectors';
import { serializeData } from 'app/schema-form/util/serializer';
import { fromJS } from 'immutable';
import forEach from 'lodash/forEach';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { createSelector } from 'reselect';
import type { SchemaFormSlice } from '../app-state.js';
import type { ImmutableMap } from '../common/utils/immutable-types.js';
import { getLocale, getLocaleFormConfigs } from '../schema/index.js';
import type { FormState } from '../schema-form/types.js';
import { LIST_NAME, NAME, FORM_NAME as VERSION_FORM_NAME } from './constants.js';
import type { ItemStatusMap, VersionListSlice, VersionListState, VersionSlice, VersionState } from './types.js';

export const getListState = (state: VersionListSlice): VersionListState => state[LIST_NAME];
export const getVersionContentState = (state: VersionSlice): VersionState => state[NAME];
export const getCurrentForm = (state: SchemaFormSlice): ImmutableMap<FormState> =>
  getForm(state, { formName: FORM_NAME });
export const getSelectedForm = (state: SchemaFormSlice): ImmutableMap<FormState> =>
  getForm(state, { formName: VERSION_FORM_NAME });

export const getVersionListStatus = createSelector(getListState, (state) => omit(state, 'data'));

export const getContentVersionStatus = createSelector(getVersionContentState, (state) => omit(state, 'data'));

export const getVersionList = createSelector(getListState, (state: VersionListState) => state.data.items);

export const getVersionListPagination = createSelector(getListState, (state) => omit(state.data, 'items'));

export function createAddTypeNamesTransform(projectSchema: ProjectSchemaJSON): PropertyTransform['process'] {
  return (obj, _schema, name, _shape, next) => {
    if (isRecord(obj) && typeof obj._shapeId === 'string') {
      const shape = getShapeById(projectSchema, obj._shapeId);
      if (shape) {
        return next({ ...obj, __typename: shape.name }, shape.schema, name);
      }
    }

    return obj;
  };
}

export const addTypeName = transformOneOf(createAddTypeNamesTransform);

export const getSelectedShapeId = createSelector(
  getVersionContentState,
  (versionState): string => versionState.data.content?._shapeId || versionState.data.content?._contentTypeId
);

export const getVersionSchema = createSelector(getVersionContentState, (versionState) => {
  const projectSchema = versionState.data.schema;
  return projectSchema && createInputSchema(projectSchema);
});

export const getSelectedShape = createSelector(getSelectedShapeId, getVersionSchema, (shapeId, projectSchema) =>
  projectSchema ? getShapeById(projectSchema, shapeId) : undefined
);

export const getSelectedContent = createSelector(
  [getVersionSchema, getSelectedShape, (state: VersionSlice) => state[NAME].data.content],
  (projectSchema, shape, content) =>
    fromJS(projectSchema && shape ? addTypeName(projectSchema, shape.schema, content) : {})
);

export const getVersionHistorySelectedShapeSchema = createSelector(
  getVersionSchema,
  getSelectedShape,
  (projectSchema, shape): PropertySchema | undefined =>
    projectSchema && shape ? shapeToPropertySchema(projectSchema, shape) : undefined
);

export const getSelectedVersion = createSelector(getVersionContentState, (state) => state.selectedVersion);

export const getCurrentFormContent = createSelector(getCurrentForm, (formState) => formState?.get('data'));

export const getCurrentFormSchema = createSelector(
  getCurrentForm,
  (formState) => formState?.get('schema') || fromJS({})
);

export const getSelectedVersionFormContent = createSelector(getSelectedForm, (formState) => formState?.get('data'));

export const getSelectedVersionFormSchema = createSelector(
  getSelectedForm,
  (formState) => formState?.get('schema') || fromJS({})
);

export type ContentDiff = {
  key: string;
  isRestorable: boolean;
};

const isBuiltInProperty = (key: string): boolean => key.startsWith('_');
const isRestorable = (
  selected: ImmutableMap<PropertySchema>,
  current: ImmutableMap<PropertySchema>,
  key: string
): boolean => {
  const currentProperty: ImmutableMap<{ key: string }> | undefined = current.getIn(['properties', key]);
  const selectedProperty: ImmutableMap<{ key: string }> | undefined = selected.getIn(['properties', key]);
  return Boolean(currentProperty && selectedProperty && currentProperty.get('key') === selectedProperty.get('key'));
};

export const getContentDiff = createSelector(
  getSelectedVersionFormSchema,
  getSelectedVersionFormContent,
  getCurrentFormSchema,
  getCurrentFormContent,
  (selectedSchema, selectedContent, currentSchema, currentContent) => {
    const serializedSelectedContent: Content = selectedContent && serializeData(selectedSchema, selectedContent, true);
    const serializedCurrentContent: Content = currentContent && serializeData(currentSchema, currentContent, true);
    const diff: ContentDiff[] = [];
    forEach(serializedSelectedContent, (value: Content, key: string) => {
      const contentChanged = !isEqual(value, serializedCurrentContent[key]);
      if (contentChanged && !isBuiltInProperty(key)) {
        diff.push({
          key,
          isRestorable: isRestorable(selectedSchema, currentSchema, key)
        });
      }
    });
    return diff;
  }
);

export const getDiffFormConfigs = createSelector(
  getSelectedShape,
  getVersionSchema,
  getLocale,
  getContentDiff,
  (shape, schema, locale, contentDiff): ImmutableMap<FormConfig> => {
    if (!shape || !schema) {
      return fromJS({});
    }

    let forms = fromJS(getLocaleFormConfigs(schema, locale));
    if (forms) {
      forEach(contentDiff, (diff: ContentDiff) => {
        forms = forms.setIn(
          [shape.name, 'default', 'properties', diff.key, 'propertyWrapper'],
          diff.isRestorable ? 'diffHighlight' : 'disabledDiffHighlight'
        );
      });
    }

    return forms;
  }
);

export const getVersionItemStatusMap = createSelector(getVersionListPagination, getVersionList, (pagination, list) => {
  const itemStatusMap: ItemStatusMap = {};
  for (let i = 0; i <= pagination.total - 1; i++) {
    itemStatusMap[i] = list[i] ? 'loaded' : 'loading';
  }

  return itemStatusMap;
});
